import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, ClickAwayListener, Skeleton, Typography } from '@mui/material'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import type { SetFilterValuesFuncParams } from 'ag-grid-enterprise'
import dayjs from 'dayjs'
import theme from 'theme'
import { ExclusionRulesServiceV4 } from 'services'
import { DevicesColumn, EXCLUDED_COUNT_NOT_CALCULATED, ExclusionRule, ExclusionRuleMatchType } from 'models'
import { dateFormat, hasPermission, Permission } from 'lib/utils'
import { useAuth } from '@frontegg/react'
import { useAppDispatch } from 'store/hooks'
import { ModalType, openModal } from 'store/modals/modalSlice'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'
import { useUsersLookup } from 'hooks/useUsersLookup'
import { ThemeButton } from 'ui/components/buttons'
import { ExclusionRulesButton } from '../devices/devices.styles'
import { LibraryDetailsHoverTooltip } from 'ui/components/tooltip/LibraryDetailsTooltip'
import OnyxiaTooltip from 'ui/components/tooltip/OnyxiaTooltip'
import {
  NO_DELETE_PERMISSION_MESSAGE,
  NO_EDIT_PERMISSION_MESSAGE
} from 'ui/components/modal/variants/exclusion-rule/exclusionRule.constants'
import { DTPicker } from '../devices/DTPicker'
import ControlButton from '../devices/components/ControlButton'
import ColumnPicker from '../devices/components/ColumnPicker'
import FilterChipsContainer from '../devices/components/FilterChipContainer'
import NoResultsFound from '../devices/components/NoResultsFound'
import SetFilterCellRenderer from '../devices/components/SetFilterCellRenderer'
import type { ActiveFilter, DateFilterValue } from '../devices/Devices'
import {
  HeaderActions,
  InfoRow,
  RulesCard,
  RulesEmptyState,
  RulesSelectionActionBar,
  RulesViewContainer,
  ViewHeader
} from './exclusionRulesView.styles'
import RulesActionsCellRenderer from './rulesActionsCellRenderer'

const DEFAULT_RULE_COLUMNS: DevicesColumn[] = [
  { name: 'rule_name', displayName: 'Exclusion Rule', type: 'string', active: true },
  { name: 'created_at', displayName: 'Exclusion Date', type: 'date', active: true },
  { name: 'reason', displayName: 'Reason for Exclusion', type: 'string', active: true },
  { name: 'match_type', displayName: 'Match', type: 'string', active: true },
  { name: 'excluded_count', displayName: 'Record Excluded', type: 'number', active: true },
  { name: 'created_by', displayName: 'Excluded by', type: 'string', active: true }
]

const ExclusionRulesIcon: FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.75 9H9.5625M6.75 11.25H9.5625M6.75 13.5H9.5625M11.8125 14.0625H13.5C14.432 14.0625 15.1875 13.307 15.1875 12.375V4.58116C15.1875 3.72993 14.5538 3.00761 13.7054 2.93721C13.4255 2.91397 13.1446 2.89394 12.8629 2.87714M8.51214 2.87714C8.46362 3.03451 8.4375 3.20171 8.4375 3.375C8.4375 3.68566 8.68934 3.9375 9 3.9375H12.375C12.6857 3.9375 12.9375 3.68566 12.9375 3.375C12.9375 3.20171 12.9114 3.03451 12.8629 2.87714M8.51214 2.87714C8.72457 2.18818 9.36631 1.6875 10.125 1.6875H11.25C12.0087 1.6875 12.6504 2.18818 12.8629 2.87714M8.51214 2.87714C8.2304 2.89394 7.94953 2.91397 7.66956 2.93721C6.82124 3.00761 6.1875 3.72993 6.1875 4.58117V6.1875M6.1875 6.1875H3.65625C3.19026 6.1875 2.8125 6.56526 2.8125 7.03125V15.4688C2.8125 15.9347 3.19026 16.3125 3.65625 16.3125H10.9688C11.4347 16.3125 11.8125 15.9347 11.8125 15.4688V7.03125C11.8125 6.56526 11.4347 6.1875 10.9688 6.1875H6.1875ZM5.0625 9H5.06813V9.00563H5.0625V9ZM5.0625 11.25H5.06813V11.2556H5.0625V11.25ZM5.0625 13.5H5.06813V13.5056H5.0625V13.5Z"
      stroke="#E9E0E4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const LOADING_ROW_COUNT = 5
const LOADING_ROWS = Array.from({ length: LOADING_ROW_COUNT }, (_, i) => ({
  __loading: true,
  id: `loading-${i}`
})) as unknown as ExclusionRule[]

const singularize = (units: string): string => (units.endsWith('s') ? units.slice(0, -1) : units)

const formatRuleDisplay = (rule: ExclusionRule | undefined | null, unitsPlural: string): string => {
  if (!rule) return ''
  if (rule.rule_name) return rule.rule_name
  const count = rule.excluded_count
  if (count === EXCLUDED_COUNT_NOT_CALCULATED || count == null) return ''
  if (count === 1) return `Exclude 1 ${singularize(unitsPlural)}`
  return `Exclude ${count} ${unitsPlural}`
}

const NOT_CALCULATED_LABEL = 'Not Calculated Yet'
const RECALCULATING_LABEL = 'Recalculating tonight'
const NOT_CALCULATED_TOOLTIP = "The rule hasn't been applied yet. Data will be updated after the next run."
const RECALCULATING_TOOLTIP = 'The rule changed after the last count. The new count comes after the next run.'

const excludedCountLabel = (rule: ExclusionRule | undefined): string => {
  if (!rule || rule.excluded_count == null) return '—'
  if (rule.excluded_count !== EXCLUDED_COUNT_NOT_CALCULATED) return String(rule.excluded_count)
  return rule.computed_at ? RECALCULATING_LABEL : NOT_CALCULATED_LABEL
}

const isPendingCount = (label: string) => label === NOT_CALCULATED_LABEL || label === RECALCULATING_LABEL

const MATCH_LABELS: Record<ExclusionRuleMatchType, string> = {
  [ExclusionRuleMatchType.All]: 'Match All',
  [ExclusionRuleMatchType.Any]: 'Match Any'
}

const matchLabel = (rule: ExclusionRule | undefined): string =>
  MATCH_LABELS[rule?.match_type ?? ExclusionRuleMatchType.All] ?? MATCH_LABELS[ExclusionRuleMatchType.All]

const ExcludedCountCell: FC<{ value: string }> = ({ value }) => {
  if (isPendingCount(value)) {
    return (
      <OnyxiaTooltip
        title={value === RECALCULATING_LABEL ? RECALCULATING_TOOLTIP : NOT_CALCULATED_TOOLTIP}
        placement="bottom"
        componentsProps={{
          tooltip: {
            sx: {
              width: '275px',
              height: '57px',
              boxSizing: 'border-box',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              whiteSpace: 'pre-line',
              textAlign: 'left',
              color: '#d0c3cc !important'
            }
          }
        }}
      >
        <span>{value}</span>
      </OnyxiaTooltip>
    )
  }
  return <span>{value}</span>
}

const SkeletonCell: FC = () => (
  <Skeleton
    variant="rectangular"
    width="70%"
    height={10}
    animation="wave"
    sx={{
      bgcolor: 'rgba(255, 255, 255, 0.04)',
      borderRadius: '4px',
      '&::after': {
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)'
      }
    }}
  />
)

const NoRulesOverlay: FC = () => (
  <RulesEmptyState className="ag-overlay-loading-center" role="presentation">
    <Box className="icon-circle">
      <ExclusionRulesIcon size={40} />
    </Box>
    <Typography className="title">No Exclusion Rules Yet</Typography>
    <Typography className="description">
      Records matching these rules are excluded from calculations. Use the “Create Exclusion Rule” button to add a new
      rule
    </Typography>
  </RulesEmptyState>
)

interface ExclusionRulesViewProps {
  cpiId: string
  onBack?: () => void
  onRulesCountChange?: (count: number) => void
  /** Opens the rule builder with no prefilled conditions. Receives the list refresh to run on save. */
  onCreateRule?: (onCreated: () => void) => void
  /** Why rule creation is unavailable, e.g. a CPI with no records. Disables the create button. */
  createBlockedReason?: string | null
  /** Opens the rule builder on an existing rule. Receives the list refresh to run on save. */
  onEditRule?: (rule: ExclusionRule, onSaved: () => void) => void
}

const ExclusionRulesView: FC<ExclusionRulesViewProps> = ({
  cpiId,
  onBack,
  onRulesCountChange,
  onCreateRule,
  createBlockedReason,
  onEditRule
}) => {
  const { user: authUser } = useAuth()
  const dispatch = useAppDispatch()
  const { metricManifest, snowflakeManifest, defaultRecentValueDate, dataSourceOptions } = useLibraryDetails()
  const canDelete = hasPermission(Permission.ExclusionRulesDelete, authUser as any)
  const canEdit = hasPermission(Permission.ExclusionRulesEdit, authUser as any)
  const gridRef = useRef<AgGridReact<ExclusionRule>>(null)
  const [rules, setRules] = useState<ExclusionRule[]>([])
  const [filterModel, setFilterModel] = useState<Record<string, any>>({})
  const [filteredCount, setFilteredCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedRules, setSelectedRules] = useState<ExclusionRule[]>([])
  const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLElement | null>(null)
  const [columnsPopperOpen, setColumnsPopperOpen] = useState(false)
  const [columns, setColumns] = useState<DevicesColumn[]>(DEFAULT_RULE_COLUMNS)

  const userIds = useMemo(() => Array.from(new Set(rules.map((r) => r.created_by).filter(Boolean))), [rules])
  const { getUserLabel } = useUsersLookup(userIds)

  const isHidden = useCallback((name: string) => columns.find((c) => c.name === name)?.active === false, [columns])

  const handleChangeColumns = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const columnName = event.target.name
    const isEnabling = event.target.checked
    setColumns((prev) => prev.map((c) => (c.name === columnName ? { ...c, active: isEnabling } : c)))
  }, [])

  const handleRestoreDefaultColumns = useCallback(() => {
    setColumns(DEFAULT_RULE_COLUMNS)
  }, [])

  const fetchRules = useCallback(async () => {
    setLoading(true)
    try {
      const res = await ExclusionRulesServiceV4.listRules(cpiId)
      const next = res.data.rules || []
      setRules(next)
      onRulesCountChange?.(next.length)
    } catch (e) {
      console.error(e)
      setRules([])
      onRulesCountChange?.(0)
    } finally {
      setLoading(false)
    }
  }, [cpiId, onRulesCountChange])

  useEffect(() => {
    fetchRules()
  }, [fetchRules])

  const handleCreateRule = useCallback(() => {
    onCreateRule?.(fetchRules)
  }, [onCreateRule, fetchRules])

  const createTooltip = canEdit ? createBlockedReason ?? '' : NO_EDIT_PERMISSION_MESSAGE

  const handleRowClick = useCallback(
    (rule: ExclusionRule) => {
      dispatch(
        openModal({
          type: ModalType.excludedRecords,
          props: {
            excludedRecordsProps: {
              ruleId: rule.id,
              cpiSlug: rule.scope_id,
              ruleName: rule.rule_name,
              reason: rule.reason,
              createdAt: rule.created_at,
              createdBy: rule.created_by,
              lastEditedAt: rule.last_edited_at ?? rule.updated_at,
              updatedBy: rule.updated_by,
              inputSchema: metricManifest?.druid_spec?.record_batch_spec?.input_schema,
              lastUpdated: defaultRecentValueDate?.date || null,
              dataSourceOptions,
              onEdit: canEdit && onEditRule ? () => onEditRule(rule, fetchRules) : undefined
            }
          }
        })
      )
    },
    [dispatch, metricManifest, defaultRecentValueDate?.date, dataSourceOptions, canEdit, onEditRule, fetchRules]
  )

  const handleEdit = useCallback(
    (rule: ExclusionRule, e: React.MouseEvent) => {
      e.stopPropagation()
      if (!canEdit) return
      onEditRule?.(rule, fetchRules)
    },
    [canEdit, onEditRule, fetchRules]
  )

  const handleDelete = useCallback(
    (rule: ExclusionRule, e: React.MouseEvent) => {
      e.stopPropagation()
      if (!canDelete) return
      dispatch(
        openModal({
          type: ModalType.deleteExclusionRule,
          props: {
            deleteExclusionRuleProps: {
              ruleIds: [rule.id],
              ruleName: rule.rule_name,
              onSuccess: () => {
                gridRef.current?.api?.deselectAll()
                setSelectedRules([])
                fetchRules()
              }
            }
          }
        })
      )
    },
    [canDelete, dispatch, fetchRules]
  )

  const handleBulkDelete = () => {
    if (!canDelete || selectedRules.length === 0) return
    dispatch(
      openModal({
        type: ModalType.deleteExclusionRule,
        props: {
          deleteExclusionRuleProps: {
            ruleIds: selectedRules.map((r) => r.id),
            ruleName: selectedRules.length === 1 ? selectedRules[0].rule_name : undefined,
            onSuccess: () => {
              gridRef.current?.api?.deselectAll()
              setSelectedRules([])
              fetchRules()
            }
          }
        }
      })
    )
  }

  const withLoadingRenderer = useCallback(
    <T extends ColDef<ExclusionRule>>(col: T): T => ({
      ...col,
      cellRendererSelector: (params: any) => {
        if (params.data?.__loading) {
          return { component: SkeletonCell }
        }
        return col.cellRenderer ? { component: col.cellRenderer, params: col.cellRendererParams } : undefined
      }
    }),
    []
  )

  const colDefs = useMemo<ColDef<ExclusionRule>[]>(
    () => [
      withLoadingRenderer({
        colId: 'rule_name',
        headerName: 'Exclusion Rule',
        flex: 2,
        minWidth: 200,
        filter: 'agSetColumnFilter',
        sortable: true,
        hide: isHidden('rule_name'),
        valueGetter: (params) => formatRuleDisplay(params.data, snowflakeManifest?.units || 'records'),
        filterParams: {
          includeBlankValues: true,
          suppressMiniFilter: false,
          defaultToNothingSelected: true,
          cellHeight: 38,
          /* Rule names run past the filter's width, so the option needs the full text on hover. */
          cellRenderer: SetFilterCellRenderer,
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          values: (params: SetFilterValuesFuncParams) => {
            const collected = new Set<string>()
            const units = snowflakeManifest?.units || 'records'
            params.api.forEachNode((node) => {
              const data: any = node.data
              if (!data || data.__loading) return
              const v = formatRuleDisplay(data, units)
              if (v) collected.add(v)
            })
            params.success(Array.from(collected).sort())
          }
        }
      }),
      withLoadingRenderer({
        field: 'created_at',
        headerName: 'Exclusion Date',
        flex: 1.2,
        minWidth: 180,
        filter: 'agDateColumnFilter',
        sortable: true,
        hide: isHidden('created_at'),
        valueFormatter: (params) => (params.value ? dayjs(params.value).format(dateFormat.customLabelHour) : '—'),
        filterParams: {
          dateFormat: 'mm/dd/yy',
          resizable: false,
          suppressAndOrCondition: true,
          suppressMiniFilter: true,
          defaultToNothingSelected: true,
          cellHeight: 38,
          buttons: ['reset'],
          comparator: (filterLocalDateAtMidnight: Date, cellValue: unknown) => {
            if (cellValue == null || cellValue === '') return -1
            const cellDate = dayjs(cellValue as string)
              .startOf('day')
              .toDate()
            const filterDay = dayjs(filterLocalDateAtMidnight).startOf('day').toDate()
            const diff = cellDate.getTime() - filterDay.getTime()
            if (diff < 0) return -1
            if (diff > 0) return 1
            return 0
          },
          maxNumConditions: 1,
          minValidYear: 2000,
          maxValidYear: 2100,
          inRangeFloatingFilterDateFormat: 'Do MMM YYYY',
          filterOptions: ['lessThan', 'greaterThan', 'inRange'],
          debounceMs: 0,
          browserDatePicker: true,
          popupPosition: 'under'
        }
      }),
      withLoadingRenderer({
        field: 'reason',
        headerName: 'Reason for Exclusion',
        flex: 3,
        minWidth: 240,
        filter: 'agSetColumnFilter',
        sortable: true,
        hide: isHidden('reason'),
        valueFormatter: (params) => params.value || '—',
        filterParams: {
          includeBlankValues: true,
          suppressMiniFilter: false,
          defaultToNothingSelected: true,
          cellHeight: 38,
          cellRenderer: SetFilterCellRenderer,
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          values: (params: SetFilterValuesFuncParams) => {
            const collected = new Set<string>()
            params.api.forEachNode((node) => {
              const data: any = node.data
              if (!data || data.__loading) return
              const v = data.reason
              if (v != null && v !== '') collected.add(String(v))
            })
            params.success(Array.from(collected).sort())
          }
        }
      }),
      withLoadingRenderer({
        colId: 'match_type',
        headerName: 'Match',
        flex: 1,
        minWidth: 130,
        filter: 'agSetColumnFilter',
        sortable: true,
        hide: isHidden('match_type'),
        valueGetter: (params) => matchLabel(params.data),
        filterParams: {
          suppressMiniFilter: true,
          defaultToNothingSelected: true,
          cellHeight: 38,
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          values: Object.values(MATCH_LABELS)
        }
      }),
      withLoadingRenderer({
        colId: 'excluded_count',
        headerName: 'Record Excluded',
        flex: 1,
        minWidth: 160,
        filter: 'agSetColumnFilter',
        sortable: true,
        hide: isHidden('excluded_count'),
        cellRenderer: ExcludedCountCell,
        valueGetter: (params) => excludedCountLabel(params.data),
        // Pending counts sort after every real count.
        comparator: (_a, _b, nodeA, nodeB) => {
          const rank = (n: number | undefined) => (n == null || n < 0 ? Number.MAX_SAFE_INTEGER : n)
          return rank(nodeA?.data?.excluded_count) - rank(nodeB?.data?.excluded_count)
        },
        filterParams: {
          includeBlankValues: true,
          suppressMiniFilter: false,
          defaultToNothingSelected: true,
          cellHeight: 38,
          cellRenderer: SetFilterCellRenderer,
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          values: (params: SetFilterValuesFuncParams) => {
            const collected = new Set<string>()
            params.api.forEachNode((node) => {
              const data: any = node.data
              if (!data || data.__loading) return
              if (typeof data.excluded_count !== 'number') return
              collected.add(excludedCountLabel(data))
            })
            const sorted = Array.from(collected).sort((a, b) => {
              if (isPendingCount(a) || isPendingCount(b)) return Number(isPendingCount(a)) - Number(isPendingCount(b))
              return Number(a) - Number(b)
            })
            params.success(sorted)
          }
        }
      }),
      withLoadingRenderer({
        field: 'created_by',
        headerName: 'Excluded by',
        flex: 1,
        minWidth: 140,
        filter: 'agSetColumnFilter',
        sortable: true,
        hide: isHidden('created_by'),
        valueGetter: (params) => getUserLabel(params.data?.created_by),
        filterParams: {
          includeBlankValues: true,
          suppressMiniFilter: false,
          defaultToNothingSelected: true,
          cellHeight: 38,
          cellRenderer: SetFilterCellRenderer,
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          values: (params: SetFilterValuesFuncParams) => {
            const collected = new Set<string>()
            params.api.forEachNode((node) => {
              const data: any = node.data
              if (!data || data.__loading || !data.created_by) return
              collected.add(getUserLabel(data.created_by))
            })
            params.success(Array.from(collected).sort())
          }
        }
      }),
      {
        colId: 'actions',
        headerName: '',
        width: 110,
        minWidth: 110,
        maxWidth: 110,
        pinned: 'right',
        lockPosition: true,
        suppressMovable: true,
        sortable: false,
        filter: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        cellRendererSelector: (params: any) => {
          if (params.data?.__loading) return undefined
          return {
            component: RulesActionsCellRenderer,
            params: { onEdit: handleEdit, onDelete: handleDelete, canEdit, canDelete }
          }
        },
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: 0,
          paddingRight: '8px'
        }
      }
    ],
    [
      handleEdit,
      handleDelete,
      canEdit,
      canDelete,
      withLoadingRenderer,
      getUserLabel,
      isHidden,
      snowflakeManifest?.units
    ]
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      minWidth: 100,
      suppressHeaderMenuButton: true
    }),
    []
  )

  const gridComponents = useMemo(() => ({ agDateInput: DTPicker }), [])

  const FIELD_TO_HEADER = useMemo<Record<string, string>>(
    () => ({
      rule_name: 'Exclusion Rule',
      created_at: 'Exclusion Date',
      reason: 'Reason for Exclusion',
      match_type: 'Match',
      excluded_count: 'Record Excluded',
      created_by: 'Excluded by'
    }),
    []
  )

  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const dateTypeMap: Record<string, string> = {
      lessThan: 'before',
      greaterThan: 'after',
      inRange: 'inRange'
    }
    return Object.entries(filterModel).map(([field, value]: [string, any]) => {
      const filterName = FIELD_TO_HEADER[field] || field
      let filterValue: string[] | DateFilterValue
      if (value?.filterType === 'set') {
        filterValue = (value.values || []) as string[]
      } else if (value?.filterType === 'date') {
        filterValue = {
          dateFrom: value.dateFrom ?? null,
          dateTo: value.dateTo ?? null,
          filterType: 'date',
          type: dateTypeMap[value.type] || value.type
        }
      } else {
        filterValue = []
      }
      return { filterName, filterValue }
    })
  }, [filterModel, FIELD_TO_HEADER])

  const handleFilterChanged = useCallback((event: any) => {
    setFilterModel(event.api.getFilterModel() || {})
    const count = event.api.getDisplayedRowCount()
    setFilteredCount(count)
    // AG Grid only auto-shows the no-rows overlay when rowData is empty.
    // Toggle manually so we also see it when filters reduce visible rows to 0.
    if (count === 0) {
      event.api.showNoRowsOverlay()
    } else {
      event.api.hideOverlay()
    }
  }, [])

  const handleResetFilter = useCallback(
    (colName: string) => {
      if (colName === 'all') {
        gridRef.current?.api?.setFilterModel(null)
        return
      }
      const field = Object.entries(FIELD_TO_HEADER).find(([, header]) => header === colName)?.[0]
      if (!field) return
      const next = { ...filterModel }
      delete next[field]
      gridRef.current?.api?.setFilterModel(Object.keys(next).length ? next : null)
    },
    [filterModel, FIELD_TO_HEADER]
  )

  return (
    <RulesViewContainer className="devices-table-container">
      <RulesCard>
        <ViewHeader>
          <Box className="heading">
            <Box className="title-row">
              <ExclusionRulesIcon />
              <Typography className="title">Exclusion Rules</Typography>
            </Box>
            <Typography className="subtitle">
              Records under these rules are ignored in calculations. Remove a rule to restore them.
            </Typography>
          </Box>
          {activeFilters.length > 0 && (
            <Box className="filters-slot">
              <FilterChipsContainer activeFilters={activeFilters} handleResetFilter={handleResetFilter} />
            </Box>
          )}
          <HeaderActions>
            {onCreateRule && (
              <OnyxiaTooltip title={createTooltip} placement="bottom-end" arrow={false}>
                <span>
                  <ThemeButton
                    className="exclusion-rule-cta"
                    themevariant="primary"
                    shape="rectangular"
                    disabled={!canEdit || Boolean(createBlockedReason)}
                    onClick={handleCreateRule}
                  >
                    Create Exclusion Rule
                  </ThemeButton>
                </span>
              </OnyxiaTooltip>
            )}
            {onBack && (
              <ExclusionRulesButton
                role="button"
                tabIndex={0}
                bordered="true"
                onClick={onBack}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onBack()
                }}
              >
                Back to Main List
              </ExclusionRulesButton>
            )}
            <ClickAwayListener
              onClickAway={() => {
                setColumnsPopperOpen(false)
                setPopperAnchorEl(null)
              }}
            >
              <Box sx={{ display: 'inline-flex' }}>
                <LibraryDetailsHoverTooltip title="Manage Columns" placement="bottom-start">
                  <div>
                    <ControlButton
                      type="columns"
                      setAnchorEl={setPopperAnchorEl}
                      handleClick={() => setColumnsPopperOpen((prev) => !prev)}
                      open={columnsPopperOpen}
                    />
                  </div>
                </LibraryDetailsHoverTooltip>
                <ColumnPicker
                  anchorEl={popperAnchorEl}
                  open={columnsPopperOpen}
                  columns={columns}
                  handleChangeColumns={handleChangeColumns}
                  handleRestoreDefaultColumns={handleRestoreDefaultColumns}
                />
              </Box>
            </ClickAwayListener>
          </HeaderActions>
        </ViewHeader>

        <InfoRow sx={{ padding: `${theme.spacing.small}px 0 ${theme.spacing.md}px 0` }}>
          <Typography>
            Number of rows:{' '}
            <span>
              {loading ? '…' : activeFilters.length > 0 && filteredCount !== null ? filteredCount : rules.length}
            </span>
          </Typography>
          {defaultRecentValueDate?.date && (
            <Typography>
              Last Update: <span>{dayjs(defaultRecentValueDate.date).format(dateFormat.lastUpdate)}</span>
            </Typography>
          )}
        </InfoRow>

        <Box sx={{ flex: 1, position: 'relative' }}>
          <AgGridReact<ExclusionRule>
            ref={gridRef}
            rowData={loading ? LOADING_ROWS : rules}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            components={gridComponents}
            rowHeight={64}
            rowStyle={{ maxHeight: '60px' }}
            headerHeight={49}
            rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true } as any}
            onSelectionChanged={(event) => {
              setSelectedRules(event.api.getSelectedRows() as ExclusionRule[])
            }}
            onFilterChanged={handleFilterChanged}
            onRowClicked={(row) => {
              if (loading || (row.data as any)?.__loading) return
              const target = row.event?.target as HTMLElement
              if (target) {
                const isActions = target.closest('[col-id="actions"]') !== null
                const isCheckbox = target.closest('.ag-selection-checkbox') !== null
                if (isActions || isCheckbox) return
              }
              if (row.data) handleRowClick(row.data)
            }}
            loading={false}
            isRowSelectable={(node) => !(node.data as any)?.__loading}
            animateRows={false}
            suppressContextMenu
            suppressScrollOnNewData
            suppressNoRowsOverlay={loading}
            noRowsOverlayComponent={() =>
              rules.length === 0 ? <NoRulesOverlay /> : <NoResultsFound blank={Object.keys(filterModel).length === 0} />
            }
          />
        </Box>

        {selectedRules.length > 0 && (
          <RulesSelectionActionBar>
            <Typography className="selection-count">
              Selected:{' '}
              <span>
                {selectedRules.length} {selectedRules.length === 1 ? 'exclusion rule' : 'exclusion rules'}
              </span>
            </Typography>
            <OnyxiaTooltip title={canDelete ? '' : NO_DELETE_PERMISSION_MESSAGE} placement="top-end" arrow={false}>
              <span>
                <ThemeButton
                  themevariant="primary"
                  shape="rectangular"
                  disabled={!canDelete}
                  onClick={handleBulkDelete}
                >
                  Delete Exclusion Rule
                </ThemeButton>
              </span>
            </OnyxiaTooltip>
          </RulesSelectionActionBar>
        )}
      </RulesCard>
    </RulesViewContainer>
  )
}

export default ExclusionRulesView
